/* eslint-disable no-case-declarations */
import { useState, useEffect } from 'react';
import Select from '../../../../juristec-ui/core/Select';
import Button from '../../../../juristec-ui/core/Button';
import {
  ColumnFilter, DateFormat, FilterColumn, FilterConfig,
  FilterFormat,
} from '../types';
import KpiFilter from '../../../KpiFilter';
import KpiFilterApplied from '../../../KpiFilterApplied';
import {
  filterTypeOptionsDate,
  filterTypeOptionsNumber,
  filterTypeOptionsPeriod,
  filterTypeOptionsSimple,
  filterTypeOptionsText,
} from '../../../../options';
import {
  FiltersContainer,
  ColumnContainer,
  BadgersContainer,
  ScrollContainer,
  OptionalLabel,
} from './styled/Filters.styled';
import { SelectOption } from '../../../../types';
import uuidv4 from '../../../../juristec-ui/utils/functions/randomUUID';

interface FiltersProps {
  fileColumns: SelectOption[];
  columnData: SelectOption[];
  columnToFilter: ColumnFilter;
  setColumnToFilter: (filter: ColumnFilter) => void;
  filterColumns: FilterColumn[];
  setFilterColumns: (filters: FilterColumn[]) => void;
  dateFormat: DateFormat;
  setDateFormat: (format: DateFormat) => void;
  dateOptions: DateFormat[];
}

/**
 * Second step for the 'CopyDashboard'. This is where the filters for the columns can be applied.
 */
const Filters = ({
  fileColumns,
  columnData,
  columnToFilter,
  setColumnToFilter,
  filterColumns,
  setFilterColumns,
  dateFormat,
  setDateFormat,
  dateOptions,
}: FiltersProps) => {
  const [filterConfig, setFilterConfig] = useState<FilterConfig>({
    column: undefined,
    selector: undefined,
    type: { label: 'Simples', value: 'simple', id: 'simple' },
    format: {
      value: 'values', label: 'Valores selecionados', id: 'values', description: 'Valores selecionados',
    },
    values: [],
  });

  const [filterValuesOptions, setFilterValuesOptions] = useState<SelectOption[]>([]);

  const getTypeOptions = (isSimple: boolean, columnType?: string) => {
    if (isSimple) {
      return filterTypeOptionsSimple;
    }
    switch (columnType) {
      case 'datetime64[ns]':
        return filterTypeOptionsPeriod.concat(filterTypeOptionsDate);
      case 'float64':
        return filterTypeOptionsNumber;
      default:
        return filterTypeOptionsText;
    }
  };

  const handleFilterConfig = (key: string, val?: FilterFormat | string[] | Date[]) => {
    const newConfig = { ...filterConfig };
    let v;
    switch (key) {
      case 'type':
        v = val as FilterFormat;
        if (v) newConfig.type = v;
        newConfig.selector = null;
        newConfig.values = [];
        if (v?.value === 'simple') {
          newConfig.format = {
            value: 'values', label: 'Valores selecionados', id: 'values', description: 'Valores selecionados',
          };
        } else {
          newConfig.format = { value: '', label: '', id: '' };
        }
        break;
      case 'format':
        v = val as FilterFormat;
        if (v) newConfig.format = v;
        switch (v?.value) {
          case 'not-contains':
          case 'contains':
          case 'not-values':
          case 'values':
            newConfig.values = [];
            break;
          case 'specific_date':
            newConfig.values = [{ value: 'hoje', label: 'Hoje', id: 'today' }];
            break;
          case 'not-between':
          case 'between':
          case 'not-between_inc':
          case 'between_inc':
            if (columnToFilter.type === 'datetime64[ns]') {
              newConfig.values = [new Date(), new Date()];
            } else {
              newConfig.values = ['', ''];
            }
            break;
          case 'not-before':
          case 'before':
          case 'not-before_inc':
          case 'before_inc':
          case 'not-after':
          case 'after':
          case 'not-after_inc':
          case 'after_inc':
            newConfig.values = [new Date()];
            break;
          default:
            newConfig.values = [''];
            break;
        }
        break;
      case 'column':
        newConfig.column = val as unknown as string;
        if (newConfig.type.value === 'simple') {
          newConfig.format = {
            value: 'values', label: 'Valores selecionados', id: 'values', description: 'Valores selecionados',
          };
        } else {
          newConfig.format = { value: '', label: '', id: '' };
        }
        break;
      default:
        newConfig[key] = val;
        break;
    }
    setFilterConfig(newConfig);
  };
  interface BetweenOptions {
    start: Date
    end: Date
  }
  const handleFilterChange = (selected: SelectOption[] | SelectOption | BetweenOptions) => {
    const regexFloat = /^-{0,1}\d*\.{0,1}\d*/;
    switch (filterConfig.format.value) {
      case 'values':
      case 'not-values':
        if (!selected || !Array.isArray(selected)) setFilterConfig({ ...filterConfig, values: [] });
        else if (selected.length === 0) setFilterConfig({ ...filterConfig, values: [] });
        else if (selected[0].value === 'select_all') setFilterConfig({ ...filterConfig, values: columnData });
        else if (selected.length > 0) {
          setFilterConfig({
            ...filterConfig,
            values: selected,
          });
        }
        break;
      case 'contains':
      case 'not-contains':
        const auxVals = new Set<string>(filterConfig.values as string[]);
        // @ts-expect-error not typed yet
        auxVals[selected.operation](selected.val);
        setFilterConfig({ ...filterConfig, values: Array.from(auxVals) });
        break;
      case 'between':
      case 'between_inc':
      case 'not-between':
      case 'not-between_inc':
        const sel = selected as BetweenOptions;
        let val1: Date | string = sel.start;
        let val2: Date | string = sel.end;
        if (columnToFilter.type !== 'datetime64[ns]') {
          val1 = sel.start?.toString()?.replace(',', '.')?.match(regexFloat)?.[0] || '';
          val2 = sel.end?.toString()?.replace(',', '.')?.match(regexFloat)?.[0] || '';
        }
        const values = filterConfig.values || [];
        if (val1 && val1 !== '') values[0] = val1;
        if (val2 && val2 !== '') values[1] = val2;
        setFilterConfig(
          { ...filterConfig, values },
        );
        break;
      case 'current_month':
      case 'current_year':
      case 'current_semester':
      case 'current_quarter':
      case 'current_week':
        handleFilterConfig('values', []);
        break;
      case 'before':
      case 'before_inc':
      case 'after':
      case 'after_inc':
      case 'not-before':
      case 'not-before_inc':
      case 'not-after':
      case 'not-after_inc':
      case 'specific_date':
        setFilterConfig({ ...filterConfig, values: [selected as SelectOption] });
        break;
      default:
        const [val] = selected.toString().match(regexFloat) || [''];
        setFilterConfig({ ...filterConfig, values: [val] });
        break;
    }
  };

  const checkValsModifiers = () => {
    switch (filterConfig.format.value) {
      case 'values':
      case 'not-values':
      case 'contains':
      case 'not-contains':
      case 'specific_date':
        return filterConfig.values.length > 0;
      case 'between':
      case 'between_inc':
      case 'not-between':
      case 'not-between_inc':
        let val1 = filterConfig.values[0] as string;
        let val2 = filterConfig.values[1] as string;
        if (columnToFilter.type !== 'datetime64[ns]') {
          if (val1?.length > 0 && val2?.length > 0) {
            val1 = Number(filterConfig.values[0]) as unknown as string;
            val2 = Number(filterConfig.values[1]) as unknown as string;
          } else {
            return false;
          }
        }
        return !Number.isNaN(val1) && !Number.isNaN(val2) && Number(val2) > Number(val1);
      case 'before':
      case 'before_inc':
      case 'after':
      case 'after_inc':
      case 'not-before':
      case 'not-before_inc':
      case 'not-after':
      case 'not-after_inc':
        return filterConfig.values[0] instanceof Date;
      case 'current_month':
      case 'current_year':
      case 'current_semester':
      case 'current_quarter':
      case 'current_week':
        return true;
      default:
        if ((filterConfig?.values?.[0] as string)?.length > 0) {
          const val = Number(filterConfig.values[0]);
          return !Number.isNaN(val);
        }
        return false;
    }
  };

  const formatValue = (
    value: Date | SelectOption | string | number,
    mFormat: string,
    isDate: boolean,
    isSimple: boolean,
  ): string | number | null => {
    if (isSimple || mFormat === 'specific_date') {
      if (value === 'select_all') return null;
      return (value as SelectOption).value;
    }
    if (isDate && [
      'between', 'between_inc', 'before', 'before_inc', 'after', 'after_inc',
      'not-between', 'not-between_inc', 'not-before', 'not-before_inc', 'not-after', 'not-after_inc',
    ].includes(mFormat)) {
      return (value as Date).toISOString().split('T')[0]; // format as YYYY-MM-DD
    }
    if (!['contains', 'not-contains'].includes(mFormat)) {
      return Number(value);
    }
    return value as string | number;
  };

  const getSelectorByType = (ftype: string): string => {
    switch (ftype) {
      case 'lastY':
      case 'not-lastY':
      case 'nextY':
      case 'not-nextY':
      case 'previousY':
      case 'not-previousY':
      case 'current_and_previousY':
      case 'not-current_and_previousY':
      case 'followingY':
      case 'not-followingY':
      case 'current_and_followingY':
      case 'not-current_and_followingY':
        return 'year';
      case 'lastM':
      case 'not-lastM':
      case 'nextM':
      case 'not-nextM':
      case 'previousM':
      case 'not-previousM':
      case 'current_and_previousM':
      case 'not-current_and_previousM':
      case 'followingM':
      case 'not-followingM':
      case 'current_and_followingM':
      case 'not-current_and_followingM':
        return 'M';
      case 'lastD':
      case 'not-lastD':
      case 'nextD':
      case 'not-nextD':
      case 'previousD':
      case 'not-previousD':
      case 'current_and_previousD':
      case 'not-current_and_previousD':
      case 'followingD':
      case 'not-followingD':
      case 'current_and_followingD':
      case 'not-current_and_followingD':
        return 'D';
      case 'lastQ':
      case 'not-lastQ':
      case 'nextQ':
      case 'not-nextQ':
      case 'previousQ':
      case 'not-previousQ':
      case 'current_and_previousQ':
      case 'not-current_and_previousQ':
      case 'followingQ':
      case 'not-followingQ':
      case 'current_and_followingQ':
      case 'not-current_and_followingQ':
        return 'Q';
      case 'lastW':
      case 'not-lastW':
      case 'nextW':
      case 'not-nextW':
      case 'previousW':
      case 'not-previousW':
      case 'current_and_previousW':
      case 'not-current_and_previousW':
      case 'followingW':
      case 'not-followingW':
      case 'current_and_followingW':
      case 'not-current_and_followingW':
        return 'W';
      case 'lastS':
      case 'not-lastS':
      case 'nextS':
      case 'not-nextS':
      case 'previousS':
      case 'not-previousS':
      case 'current_and_previousS':
      case 'not-current_and_previousS':
      case 'followingS':
      case 'not-followingS':
      case 'current_and_followingS':
      case 'not-current_and_followingS':
        return 'semester';
      default:
        return '';
    }
  };

  const getActualTypeAndInverse = (ftype: string): [boolean, string] => {
    const ftypeInfo = ftype.split('-');
    let actualFtype = ftypeInfo?.[1] || ftypeInfo?.[0];
    if (['lastY', 'lastM', 'lastD', 'lastQ', 'lastW', 'lastS'].includes(actualFtype)) {
      actualFtype = 'last';
    } else if (['nextY', 'nextM', 'nextD', 'nextQ', 'nextW', 'nextS'].includes(actualFtype)) {
      actualFtype = 'next';
    } else if (['previousY', 'previousM', 'previousD', 'previousQ', 'previousW', 'previousS'].includes(actualFtype)) {
      actualFtype = 'previous';
    } else if (['followingY', 'followingM', 'followingD', 'followingQ', 'followingW', 'followingS'].includes(actualFtype)) {
      actualFtype = 'following';
    } else if (['current_and_previousY', 'current_and_previousM', 'current_and_previousD', 'current_and_previousQ', 'current_and_previousW', 'current_and_previousS'].includes(actualFtype)) {
      actualFtype = 'current_and_previous';
    } else if (['current_and_followingY', 'current_and_followingM', 'current_and_followingD', 'current_and_followingQ', 'current_and_followingW', 'current_and_followingS'].includes(actualFtype)) {
      actualFtype = 'current_and_following';
    }
    return [
      ftypeInfo?.[0] === 'not',
      actualFtype,
    ];
  };

  const applyFilter = () => {
    if (filterConfig.column && checkValsModifiers()) {
      const columnType = columnToFilter?.type;
      const isSimple = filterConfig?.type?.value === 'simple';
      const [isInverse, actualType] = getActualTypeAndInverse(filterConfig?.format?.value);

      const newFilter: FilterColumn = {
        column: filterConfig.column as string,
        type: columnType,
        ftype: actualType || 'values',
        selector: isSimple ? (filterConfig?.selector?.value || '') : getSelectorByType(filterConfig.format.value),
        values: filterConfig.values.reduce(
          (
            aux: (string | number)[],
            v: SelectOption | number | Date | string,
          ) => {
            const val = formatValue(v, actualType, columnType === 'datetime64[ns]', isSimple);
            if (val || val === 0 || val === '') aux.push(val);
            return aux;
          }, [] as (string | number)[],
        ),
        uid: uuidv4(),
        inverse: isInverse,
      };

      setFilterColumns([...filterColumns, newFilter]);
      setFilterConfig(
        {
          column: null,
          selector: null,
          type: { label: 'Simples', value: 'simple', id: 'simple' },
          format: { value: 'values', label: 'Valores selecionados', id: 'values' },
          values: [],
        },
      );
      setColumnToFilter({ value: '', label: '' });
    }
  };

  useEffect(() => {
    if (filterConfig?.values?.length > 0) {
      handleFilterConfig('values');
    }
  }, [filterValuesOptions]);

  useEffect(() => {
    setFilterConfig({ ...filterConfig, column: columnToFilter?.value });
  }, [columnToFilter]);

  useEffect(() => {
    if (columnData?.length > 0 && filterConfig?.column) {
      setFilterValuesOptions(columnData);
    }
  }, [columnData, filterConfig.column]);

  const removeFilterHandle = (filterUid: string) => {
    setFilterColumns(filterColumns?.filter((item) => item?.uid !== filterUid));
  };

  const editFilterHandle = (
    filterUid: string,
    filterColumn: FilterColumn,
    newValues: string[] | number[] | Date[] | SelectOption[],
    ftype: string,
  ) => {
    if (newValues.length === 0) return;
    setFilterColumns(filterColumns.map((item) => (
      item.uid === filterUid ? {
        ...item,
        values: newValues.map((v) => (
          formatValue(v, ftype, filterColumn?.type === 'datetime64[ns]', ftype === 'values') as (string | number)
        )),
      } : item
    )));
  };

  const changeOrderModifiers = (pos: number, varOp: number) => {
    const newPos = pos + varOp;
    if (newPos < 0 && newPos >= filterColumns.length) return;
    const tempList = [...filterColumns];
    const [auxItem] = tempList.splice(pos, 1);
    tempList.splice(newPos, 0, auxItem);
    setFilterColumns(tempList);
  };

  const changeFilterOrder = (pos: number, varOp: number) => {
    changeOrderModifiers(pos, varOp);
  };
  return (
    <>
      <OptionalLabel>Não é obrigatório a utilização do filtro!</OptionalLabel>
      <FiltersContainer>
        <ColumnContainer style={{ gap: '10px' }}>
          <Select
            placement="start"
            selectLabel="Coluna para filtrar"
            options={fileColumns}
            // @ts-expect-error not typed yet
            value={columnToFilter}
            // @ts-expect-error not typed yet
            onChange={(selected: SelectOption) => setColumnToFilter(selected)}
            placeholder=""
            alphabeticalOrder
            isSearchable
            fullWidth
            atModal
          />
          {columnToFilter.value?.length > 0 && (
            <>
              <div style={{ display: 'flex', gap: '10px' }}>
                <Select
                  selectLabel="Tipo"
                  placeholder="Selecione"
                  // @ts-expect-error not typed yet
                  onChange={(newValue: SelectOption) => handleFilterConfig('type', newValue)}
                  // @ts-expect-error not typed yet
                  value={filterConfig?.type}
                  options={[
                    { label: 'Simples', value: 'simple', id: 'simple' },
                    { label: 'Avançado', value: 'advanced', id: 'advanced' },
                  ]}
                  formatOptionLabel={false}
                  fullWidth
                  atModal
                />
                {columnToFilter?.type === 'datetime64[ns]' && filterConfig?.type?.value === 'simple' && (
                  <Select
                    placement="start"
                    selectLabel="Formato"
                    options={dateOptions}
                    // @ts-expect-error not typed yet
                    value={dateFormat}
                    // @ts-expect-error not typed yet
                    onChange={(selected: SelectOption) => setDateFormat(selected)}
                    placeholder=""
                    alphabeticalOrder
                    isSearchable
                    fullWidth
                    atModal
                  />
                )}
              </div>

              <Select
                placement="start"
                selectLabel="Condição"
                isSearchable
                // @ts-expect-error not typed yet
                onChange={(newValue: SelectOption) => handleFilterConfig('format', newValue)}
                // @ts-expect-error not typed yet
                value={filterConfig.format}
                options={getTypeOptions(filterConfig?.type?.value === 'simple', columnToFilter?.type)}
                placeholder="Selecione"
                formatOptionLabel={false}
                fullWidth
                atModal
              />

              {
                filterConfig.format.value !== ''
                && !filterTypeOptionsPeriod.some((f) => (filterConfig?.format?.value === f?.value))
                && (
                  <KpiFilter
                    filterType={filterConfig.format.value}
                    // @ts-expect-error not typed yet
                    handle={handleFilterChange}
                    // @ts-expect-error not typed yet
                    values={filterConfig.values}
                    // @ts-expect-error not typed yet
                    options={filterValuesOptions}
                    format={columnToFilter.type}
                    atModal
                  />
                )
              }

              <Button
                disabled={!checkValsModifiers()}
                onClick={applyFilter}
                color="secondary"
              >
                Adicionar filtro
              </Button>
            </>
          )}
        </ColumnContainer>
        <ColumnContainer>
          <span className="label">Filtros Aplicados:</span>
          <ScrollContainer>
            {filterColumns?.length > 0 ? (
              filterColumns.map((filterC, i) => (
                <KpiFilterApplied
                  key={filterC.uid}
                  // @ts-expect-error not typed yet
                  modifier={filterC}
                  // @ts-expect-error not typed yet
                  removeFunc={removeFilterHandle}
                  // @ts-expect-error not typed yet
                  editFunc={editFilterHandle}
                  getOptions={() => columnData}
                  dataFrameTypes={fileColumns.reduce(
                    (acc, col) => ({ ...acc, [col.label]: col.type }), {},
                  )}
                  position={i}
                  isLast={filterColumns.length - 1 === i}
                  // @ts-expect-error not typed yet
                  changePosition={changeFilterOrder}
                />
              ))
            ) : (
              <BadgersContainer>
                <div className="badgersHeader">
                  <span>Nenhum filtro selecionado</span>
                </div>
              </BadgersContainer>
            )}
          </ScrollContainer>
        </ColumnContainer>
      </FiltersContainer>
    </>
  );
};

export default Filters;
